import { Breadcrumb, InlineAlert, PageHeader, PageTitleBlock } from '@planuze/ui';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { DetailPageState } from '../components/detail-page-state.js';
import { ChatComposer } from '../components/support/chat-composer.js';
import { ChatHeader } from '../components/support/chat-header.js';
import { ChatThread } from '../components/support/chat-thread.js';
import { TicketContextPanel } from '../components/support/ticket-context-panel.js';
import { TypingIndicator } from '../components/support/typing-indicator.js';
import { useSupportChatContent } from '../content/sections/support-chat.js';
import { useChatWebsocket } from '../hooks/use-chat-websocket.js';
import { useStaffSession } from '../hooks/use-staff-session.js';
import { useTicketDetail } from '../hooks/use-tickets.js';

const routeApi = getRouteApi('/authenticated/support/$ticketId');

export function SupportChatPage() {
  const { ticketId } = routeApi.useParams();
  const session = useStaffSession();
  const navigate = useNavigate();
  const copy = useSupportChatContent();
  const { data: ticket, isLoading, isError } = useTicketDetail(ticketId);
  const { messages, typingSenders, connectionState, sendMessage, sendTyping, markRead } =
    useChatWebsocket(ticketId);

  /* Marca como lida a última mensagem recebida do usuário (mark-read do ADR-0352). */
  const lastMessage = messages.at(-1);
  useEffect(() => {
    if (
      connectionState === 'open' &&
      lastMessage !== undefined &&
      lastMessage.senderType === 'user'
    ) {
      markRead(lastMessage.id);
    }
  }, [connectionState, lastMessage, markRead]);

  const renderHeader = () => (
    <PageHeader>
      <PageTitleBlock>
        <Breadcrumb
          ariaLabel={copy.breadcrumbAriaLabel}
          items={[
            {
              label: copy.supportLabel,
              onClick: () => {
                navigate({ to: '/support' }).catch(() => undefined);
              },
            },
            { label: copy.ticketLabel },
          ]}
        />
      </PageTitleBlock>
    </PageHeader>
  );

  if (isError) {
    return <DetailPageState header={renderHeader()} kind="error" message={copy.notFoundMessage} />;
  }
  if (isLoading || ticket === undefined) {
    return <DetailPageState header={renderHeader()} kind="loading" message="" />;
  }
  /* Chat = coluna flex-1; painel de contexto = coluna fixa à direita (some em telas
     estreitas via `hidden lg:flex` no próprio painel). */
  return (
    <div className="flex h-full bg-background">
      <div className="flex min-w-0 flex-1 flex-col">
        <ChatHeader ticket={ticket} staffId={session?.id ?? ''} connectionState={connectionState} />
        {connectionState === 'reconnecting' && (
          <InlineAlert variant="warning">{copy.reconnecting}</InlineAlert>
        )}
        {connectionState === 'closed' && (
          <InlineAlert variant="destructive">{copy.connectionLost}</InlineAlert>
        )}
        <ChatThread messages={messages} />
        <TypingIndicator senders={typingSenders} />
        <ChatComposer
          onSend={(content) => sendMessage(content)}
          onTyping={sendTyping}
          disabled={connectionState !== 'open' || ticket.status === 'closed'}
        />
      </div>
      <TicketContextPanel ticket={ticket} />
    </div>
  );
}
